import { useEffect, useRef } from "react";
import { useTranslate } from "ra-core";
import { Link, useSearchParams } from "react-router";
import {
  Bot,
  CheckCircle2,
  Film,
  Hand,
  Loader2,
  RotateCcw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { Contact } from "../types";
import { FelixHandoffStatus } from "../whatsapp/inbox/FelixHandoffStatus";
import { PriorityDot, ReasonChip } from "../whatsapp/inbox/InboxBadges";
import {
  attentionReasonLabel,
  attentionToneClass,
  formatRelativeTime,
  webCreateStall,
} from "../whatsapp/inbox/inboxModel";
import {
  useInboxConversation,
  useInboxEnabled,
  useInboxTransition,
  useNow,
} from "../whatsapp/inbox/useInbox";
import { CONTACT_WHATSAPP_CLAIM_PARAM } from "./contactDeepLinks";

/**
 * Attention strip above the chat: why Felix needs a hand, how long the contact
 * has been waiting, who claimed it, and the claim / resolve / reopen actions.
 * Visiting the page with `?claim=1` claims a needs-reply conversation once and
 * strips the flag from the URL. Renders nothing without the inbox capability
 * or when the contact has no open episode.
 */
export const ContactAttentionBanner = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.whatsapp.inbox.${key}`, options);
  const enabled = useInboxEnabled();
  const query = useInboxConversation(enabled ? contact.id : null);
  const { run, release, isPendingFor } = useInboxTransition();
  const [searchParams, setSearchParams] = useSearchParams();
  const now = useNow();
  const autoClaimRef = useRef(false);

  const row = query.data;
  const state = row?.attention_state ?? "none";
  const wantsClaim = searchParams.get(CONTACT_WHATSAPP_CLAIM_PARAM) === "1";

  useEffect(() => {
    if (!wantsClaim || !row) return;
    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete(CONTACT_WHATSAPP_CLAIM_PARAM);
      setSearchParams(next, { replace: true });
    };
    if (row.attention_state !== "needs_reply") {
      clearParam();
      return;
    }
    if (autoClaimRef.current) return;
    autoClaimRef.current = true;
    void run({ action: "claim", contactId: contact.id }).finally(clearParam);
  }, [contact.id, row, run, searchParams, setSearchParams, wantsClaim]);

  if (!enabled || !row || state === "none") return null;

  const pending = isPendingFor(contact.id);
  const waiting = formatRelativeTime(row.attention_since, now);
  const count = row.attention_count ?? 0;
  // MYC-090: a stalled my.echoflicks.com creation, not a WhatsApp message.
  const webStall = webCreateStall(row);

  if (state === "resolved") {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-xs",
          attentionToneClass("resolved"),
        )}
      >
        <span>
          {row.attention_resolved_by_name
            ? t("resolved_by", { name: row.attention_resolved_by_name })
            : t("tabs.resolved")}
          {row.attention_resolved_at
            ? ` · ${formatRelativeTime(row.attention_resolved_at, now)}`
            : ""}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7"
          disabled={pending}
          onClick={() => void run({ action: "reopen", contactId: contact.id })}
        >
          <RotateCcw className="size-3.5" />
          {t("actions.reopen")}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm",
        attentionToneClass(state),
      )}
      role="status"
    >
      <PriorityDot priority={row.attention_priority} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          <span>
            {state === "claimed"
              ? t("banner.claimed", {
                  name: row.attention_claimed_by_name || t("unassigned"),
                })
              : t("banner.needs_reply")}
          </span>
          <ReasonChip label={attentionReasonLabel(row)} />
        </div>
        <div className="flex flex-wrap gap-x-3 text-xs opacity-80">
          {waiting ? (
            <span>{t("banner.waiting", { time: waiting })}</span>
          ) : null}
          {count > 0 ? (
            <span>{t("banner.messages", { smart_count: count })}</span>
          ) : null}
          {webStall ? (
            <span>{t(`web_stall.${webStall.stage ?? "unknown"}`)}</span>
          ) : row.attention_message_type ? (
            <span>{row.attention_message_type}</span>
          ) : null}
        </div>
        {row.attention_preview ? (
          <p className="truncate text-xs italic opacity-80">
            “{row.attention_preview}”
          </p>
        ) : null}
        <FelixHandoffStatus row={row} now={now} />
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {webStall?.echoPath ? (
          <Button type="button" size="sm" variant="outline" asChild>
            <Link to={webStall.echoPath}>
              <Film className="size-4" />
              {t("actions.open_echo")}
            </Link>
          </Button>
        ) : null}
        {state === "needs_reply" ? (
          <Button
            type="button"
            size="sm"
            disabled={pending}
            title={t("actions.claim_hint")}
            onClick={() => void run({ action: "claim", contactId: contact.id })}
          >
            {pending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Hand className="size-4" />
            )}
            {t("actions.claim")}
          </Button>
        ) : null}
        {state === "claimed" ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pending}
            title={t("actions.release_hint")}
            onClick={() => void release(row)}
          >
            <Bot className="size-4" />
            {t("actions.release")}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => void run({ action: "resolve", contactId: contact.id })}
        >
          <CheckCircle2 className="size-4" />
          {t("actions.resolve")}
        </Button>
      </div>
    </div>
  );
};
